import type { CanvasNode } from '@/types/visual-editor';

export interface CanvasNodeEntryFields {
  enteredCanvasAt?: string;
  lastActivityAt?: string;
  createdAt?: string;
  _preparingCreatedAt?: number;
}

function parseTimestamp(value?: string): number {
  if (!value) return Number.POSITIVE_INFINITY;
  const time = Date.parse(value);
  return Number.isFinite(time) ? time : Number.POSITIVE_INFINITY;
}

export function deriveEnteredCanvasAt(
  node: CanvasNodeEntryFields,
  fallbackMs: number = Date.now(),
): string {
  if (node.enteredCanvasAt) return node.enteredCanvasAt;
  if (node.createdAt) return node.createdAt;
  if (typeof node._preparingCreatedAt === 'number' && Number.isFinite(node._preparingCreatedAt)) {
    return new Date(node._preparingCreatedAt).toISOString();
  }
  return new Date(fallbackMs).toISOString();
}

export function ensureEnteredCanvasAt(
  node: CanvasNode,
  fallbackMs?: number,
): CanvasNode & { enteredCanvasAt: string };
export function ensureEnteredCanvasAt<T extends CanvasNodeEntryFields>(
  node: T,
  fallbackMs: number = Date.now(),
): T & { enteredCanvasAt: string } {
  const enteredCanvasAt = deriveEnteredCanvasAt(node, fallbackMs);
  if (node.enteredCanvasAt === enteredCanvasAt) {
    return node as T & { enteredCanvasAt: string };
  }
  return {
    ...node,
    enteredCanvasAt,
  };
}

export function getCanvasNodeChronologyValue(node: CanvasNodeEntryFields): number {
  const enteredCanvasAt = parseTimestamp(node.enteredCanvasAt);
  if (Number.isFinite(enteredCanvasAt)) return enteredCanvasAt;

  const createdAt = parseTimestamp(node.createdAt);
  if (Number.isFinite(createdAt)) return createdAt;

  if (typeof node._preparingCreatedAt === 'number' && Number.isFinite(node._preparingCreatedAt)) {
    return node._preparingCreatedAt;
  }

  return Number.POSITIVE_INFINITY;
}

export function deriveLastActivityAt(
  node: CanvasNodeEntryFields,
  fallbackMs: number = Date.now(),
): string {
  if (node.lastActivityAt) return node.lastActivityAt;
  return deriveEnteredCanvasAt(node, fallbackMs);
}

export function ensureLastActivityAt(
  node: CanvasNode,
  fallbackMs?: number,
): CanvasNode & { lastActivityAt: string };
export function ensureLastActivityAt<T extends CanvasNodeEntryFields>(
  node: T,
  fallbackMs: number = Date.now(),
): T & { lastActivityAt: string } {
  const lastActivityAt = deriveLastActivityAt(node, fallbackMs);
  if (node.lastActivityAt === lastActivityAt) {
    return node as T & { lastActivityAt: string };
  }
  return {
    ...node,
    lastActivityAt,
  };
}

export function getCanvasNodeActivityValue(node: CanvasNodeEntryFields): number {
  const lastActivityAt = parseTimestamp(node.lastActivityAt);
  if (Number.isFinite(lastActivityAt)) return lastActivityAt;
  return getCanvasNodeChronologyValue(node);
}

export function stampCanvasNodeActivity<T extends CanvasNodeEntryFields>(
  node: T,
  activityAt: string,
): T & { lastActivityAt: string } {
  if (node.lastActivityAt === activityAt) {
    return node as T & { lastActivityAt: string };
  }
  return {
    ...node,
    lastActivityAt: activityAt,
  };
}

export function createCanvasActivityTimestamp(nowMs: number = Date.now()): string {
  return new Date(nowMs).toISOString();
}
